/**
 * Backend estate API — typed TanStack Query hook for the tenant-wide ESTATE
 * asset view (ESTATE ownership model, 2026-07-11 Paul ruling —
 * tasks/estate-ownership-20260711/DESIGN.md; backend Agent D, FE wiring
 * Agent H).
 *
 * Wraps `GET /v1/estate/assets`
 * (`backend/src/handlers/estate-assets-list.ts`): every §16 asset record in
 * the caller's tenant, across ALL THREE ownership states, plus per-state
 * totals (whole Rand). This is the ONE read that lets an estate-only client
 * (zero trusts, everything `person_owned`) see their own assets — Hero /
 * RegistrationJourney / WealthDistribution all require a trust in scope,
 * this doesn't — and the read the dashboard's estate card + the hero's
 * split line both draw from without re-deriving sums client-side.
 *
 * A separate module (not `trusts.ts`) because this is a TENANT-level read,
 * not a per-trust one — the existing `AssetView` / `AssetOwnershipState` /
 * `assetStatusBadge` types from `trusts.ts` are reused here, not duplicated.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { AssetView } from './trusts';

/** Per-ownership-state totals, whole Rand (mirrors the backend's
 *  `EstateAssetTotals`). `estateValueRand` is the sum of all three — the
 *  ESTATE headline (DESIGN.md invariant 4): registered never absorbs the
 *  other two. */
export interface EstateAssetTotals {
  personOwnedValueRand: number;
  earmarkedValueRand: number;
  registeredValueRand: number;
  estateValueRand: number;
}

export interface EstateAssetsResponse {
  /** ALL of the tenant's §16 assets, across all 3 ownership states.
   *
   *  PER-FIRM VISIBILITY SCOPES (2026-08-23): under a firm act-as session whose
   *  client has withheld their asset list, the `person_owned` rows are absent —
   *  the private portfolio held outside any trust. Trust-anchored rows are never
   *  withheld. `withheld.assetList` says when that happened. */
  assets: AssetView[];
  /** ABSENT — not zeroed — when the client has withheld their total portfolio
   *  value from the firm acting-as (`withheld.netWorth`). A zeroed total reads
   *  as "owns nothing", which is a different and false statement; render the
   *  withheld state instead. Always present for the client's own session. */
  totals?: EstateAssetTotals;
  /** Present ONLY when a per-firm visibility scope removed something from this
   *  response. Client-owned decision, enforced server-side. */
  withheld?: { netWorth?: true; assetList?: true };
  correlationId: string;
}

/** Query key + fetcher shared by the hook AND the auth-time dashboard
 *  prefetch (`prefetch-dashboard.ts`) — one definition, so the prefetched
 *  cache entry is guaranteed to be the exact one the dashboard reads. */
export const ESTATE_ASSETS_QUERY_KEY = ['estate', 'assets'] as const;

export function fetchEstateAssets(signal?: AbortSignal): Promise<EstateAssetsResponse> {
  return api<EstateAssetsResponse>('/api/v1/estate/assets', { signal });
}

/**
 * GET /v1/estate/assets — every tenant member sees the WHOLE estate; there is
 * no narrower trust-membership gate (the estate IS the tenant — see the
 * backend handler's file header). Degrades to an honest empty estate (200,
 * zeroed totals) on a not-yet-provisioned `assets` container, the same
 * container-degrade contract as `useTrustAssets`.
 */
export function useEstateAssets(opts: { enabled?: boolean } = {}) {
  return useQuery<EstateAssetsResponse>({
    queryKey: ESTATE_ASSETS_QUERY_KEY,
    queryFn: ({ signal }) => fetchEstateAssets(signal),
    enabled: opts.enabled ?? true,
  });
}
